import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface RecordFormState {
  readonly outstanding: number;
  readonly amount: string;
  readonly setAmount: (value: string) => void;
  readonly paidDate: string;
  readonly setPaidDate: (value: string) => void;
  readonly bankRef: string;
  readonly setBankRef: (value: string) => void;
  readonly note: string;
  readonly setNote: (value: string) => void;
  readonly currency: string | null;
}

function OutstandingLine({
  outstanding,
  currency,
}: Readonly<{ outstanding: number; currency: string | null }>) {
  return (
    <div className="text-muted-foreground text-xs">
      Outstanding:{' '}
      <span className="text-foreground font-medium">
        {currency === null ? outstanding.toFixed(2) : `${outstanding.toFixed(2)} ${currency}`}
      </span>
    </div>
  );
}

function AmountFields({
  state,
  isPending,
}: Readonly<{ state: RecordFormState; isPending: boolean }>) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor="total_amount_paid">Amount paid</Label>
        <Input
          id="total_amount_paid"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          required
          value={state.amount}
          onChange={(e) => {
            state.setAmount(e.target.value);
          }}
          disabled={isPending}
          data-testid="record-payment-amount"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="paid_date">Paid date</Label>
        <Input
          id="paid_date"
          type="date"
          required
          value={state.paidDate}
          onChange={(e) => {
            state.setPaidDate(e.target.value);
          }}
          disabled={isPending}
          data-testid="record-payment-paid-date"
        />
      </div>
    </>
  );
}

function ReferenceFields({
  state,
  isPending,
}: Readonly<{ state: RecordFormState; isPending: boolean }>) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor="bank_reference">Bank reference (optional)</Label>
        <Input
          id="bank_reference"
          maxLength={256}
          value={state.bankRef}
          onChange={(e) => {
            state.setBankRef(e.target.value);
          }}
          disabled={isPending}
          data-testid="record-payment-bank-reference"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="reference_note">Note (optional)</Label>
        <Textarea
          id="reference_note"
          maxLength={1024}
          rows={3}
          value={state.note}
          onChange={(e) => {
            state.setNote(e.target.value);
          }}
          disabled={isPending}
          data-testid="record-payment-note"
        />
      </div>
    </>
  );
}

export function RecordForm({
  state,
  canSubmit,
  isPending,
  onCancel,
  onSubmit,
}: {
  readonly state: RecordFormState;
  readonly canSubmit: boolean;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
      className="space-y-3"
    >
      <OutstandingLine outstanding={state.outstanding} currency={state.currency} />
      <AmountFields state={state} isPending={isPending} />
      <ReferenceFields state={state} isPending={isPending} />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit} data-testid="record-payment-save">
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
